import React, { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../supabaseClient'

function SortHeader({ label, column, sortColumn, sortDirection, onSort, className }) {
  const isActive = sortColumn === column
  return (
    <div
      className={`${className || ''} sortable-header${isActive ? ' sort-active' : ''}`}
      onClick={() => onSort(column)}
      title={`Sort by ${label}`}
    >
      {label}
      <span className="sort-indicator">{isActive ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : ' ↕'}</span>
    </div>
  )
}

function BrowseLures() {
  const [allLures, setAllLures] = useState([])
  const [brands, setBrands] = useState([])
  const [types, setTypes] = useState([])
  const [loading, setLoading] = useState(true)

  // Filters
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedBrand, setSelectedBrand] = useState('')
  const [selectedType, setSelectedType] = useState('')

  // View + sort
  const [viewMode, setViewMode] = useState('gallery')
  const [sortColumn, setSortColumn] = useState(null)
  const [sortDirection, setSortDirection] = useState('asc')

  const GALLERY_SORT_OPTIONS = [
    { key: 'lure',      label: 'Lure Name' },
    { key: 'brand',     label: 'Brand' },
    { key: 'category',  label: 'Category' },
    { key: 'weight',    label: 'Weight' },
    { key: 'length',    label: 'Length' },
    { key: 'depth',     label: 'Depth' },
    { key: 'colors',    label: 'Colors' },
    { key: 'avgRating', label: 'Avg Rating' },
  ]

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [{ data: luresData }, { data: brandsData }, { data: typesData }] = await Promise.all([
        supabase
          .from('base_lures')
          .select(`
            base_lure_id,
            name,
            weight,
            length,
            depth_range,
            image_url,
            brands (brand_id, brand_name),
            lure_types (type_id, type_name),
            lure_variants (variant_id, color_name),
            lure_reviews (rating)
          `)
          .order('name'),
        supabase.from('brands').select('brand_id, brand_name').order('brand_name'),
        supabase.from('lure_types').select('type_id, type_name').order('type_name'),
      ])

      const processed = (luresData || []).map(lure => {
        const reviews = lure.lure_reviews || []
        const avgRating = reviews.length > 0
          ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
          : null
        return {
          ...lure,
          variantCount: lure.lure_variants?.length || 0,
          colorNames: lure.lure_variants?.map(v => v.color_name).filter(Boolean).slice(0, 3) || [],
          avgRating,
          reviewCount: reviews.length,
        }
      })

      setAllLures(processed)
      setBrands(brandsData || [])
      setTypes(typesData || [])
      setLoading(false)
    } catch (error) {
      console.error('Error fetching data:', error)
      setLoading(false)
    }
  }

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  const filteredLures = useMemo(() => {
    // 1. Filter
    let result = allLures.filter(lure => {
      if (searchTerm && !lure.name.toLowerCase().includes(searchTerm.toLowerCase())) return false
      if (selectedBrand && lure.brands?.brand_id !== parseInt(selectedBrand)) return false
      if (selectedType && lure.lure_types?.type_id !== parseInt(selectedType)) return false
      return true
    })

    // 2. Sort
    if (sortColumn) {
      result = [...result].sort((a, b) => {
        let aVal, bVal

        switch (sortColumn) {
          case 'lure':      aVal = a.name || '';                           bVal = b.name || '';                           break
          case 'brand':     aVal = a.brands?.brand_name || '';             bVal = b.brands?.brand_name || '';             break
          case 'category':  aVal = a.lure_types?.type_name || '';          bVal = b.lure_types?.type_name || '';          break
          case 'weight':    aVal = a.weight || '';                         bVal = b.weight || '';                         break
          case 'length':    aVal = a.length || '';                         bVal = b.length || '';                         break
          case 'depth':     aVal = a.depth_range || '';                    bVal = b.depth_range || '';                    break
          case 'colors':    aVal = a.variantCount;                         bVal = b.variantCount;                         break
          case 'avgRating': aVal = a.avgRating;                            bVal = b.avgRating;                            break
          default: return 0
        }

        if (aVal === null && bVal === null) return 0
        if (aVal === null) return 1
        if (bVal === null) return -1

        const cmp = typeof aVal === 'string' ? aVal.localeCompare(bVal) : aVal - bVal
        return sortDirection === 'asc' ? cmp : -cmp
      })
    }

    return result
  }, [allLures, searchTerm, selectedBrand, selectedType, sortColumn, sortDirection])

  const clearFilters = () => {
    setSearchTerm('')
    setSelectedBrand('')
    setSelectedType('')
  }

  const hasActiveFilters = searchTerm || selectedBrand || selectedType
  const formatRating = (r) => r !== null && r !== undefined ? r.toFixed(1) : '—'

  if (loading) return <div className="loading">Loading lure database...</div>

  return (
    <div className="browse-page">
      {/* Header row */}
      <div className="browse-header">
        <div>
          <h2>Browse Lures</h2>
          <p className="page-subtitle">Search and filter through our complete lure database</p>
        </div>
        <div className="view-toggle">
          <button
            className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
            title="List view"
          >
            ☰ List
          </button>
          <button
            className={`view-toggle-btn ${viewMode === 'gallery' ? 'active' : ''}`}
            onClick={() => setViewMode('gallery')}
            title="Gallery view"
          >
            ⊞ Gallery
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-panel">
        <div className="filter-group">
          <label>Search:</label>
          <input
            type="text"
            placeholder="Search lure names..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="filter-input"
          />
        </div>
        <div className="filter-group">
          <label>Brand:</label>
          <select value={selectedBrand} onChange={e => setSelectedBrand(e.target.value)} className="filter-select">
            <option value="">All Brands</option>
            {brands.map(b => <option key={b.brand_id} value={b.brand_id}>{b.brand_name}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>Type:</label>
          <select value={selectedType} onChange={e => setSelectedType(e.target.value)} className="filter-select">
            <option value="">All Types</option>
            {types.map(t => <option key={t.type_id} value={t.type_id}>{t.type_name}</option>)}
          </select>
        </div>
        {hasActiveFilters && (
          <button onClick={clearFilters} className="clear-filters-btn">Clear Filters</button>
        )}
      </div>

      {/* Results info */}
      <div className="results-info">
        Showing {filteredLures.length} of {allLures.length} lures
        {hasActiveFilters && ' (filtered)'}
        {sortColumn && (
          <span>
            {' · Sorted by '}<strong>{sortColumn}</strong>{' '}{sortDirection === 'asc' ? '▲' : '▼'}
            {' '}<button className="clear-sort-btn" onClick={() => setSortColumn(null)}>✕</button>
          </span>
        )}
      </div>

      {filteredLures.length === 0 ? (
        <div className="no-results">
          <p>No lures found matching your search criteria.</p>
          <button onClick={clearFilters} className="reset-btn">Reset Filters</button>
        </div>
      ) : viewMode === 'list' ? (
        /* ─── LIST VIEW ─── */
        <div className="browse-list">
          <div className="browse-list-header">
            <div className="bl-img"></div>
            <SortHeader label="Lure"     column="lure"      sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} className="bl-lure" />
            <SortHeader label="Brand"    column="brand"     sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} className="bl-brand" />
            <SortHeader label="Category" column="category"  sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} className="bl-category" />
            <SortHeader label="Weight"   column="weight"    sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} className="bl-attr" />
            <SortHeader label="Length"   column="length"    sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} className="bl-attr" />
            <SortHeader label="Depth"    column="depth"     sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} className="bl-attr" />
            <SortHeader label="Colors"   column="colors"    sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} className="bl-colors" />
            <SortHeader label="Avg ★"    column="avgRating" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} className="bl-rating" />
          </div>

          {filteredLures.map(lure => (
            <Link
              key={lure.base_lure_id}
              to={`/lure/${lure.base_lure_id}`}
              className="browse-list-row"
            >
              <div className="bl-img">
                {lure.image_url
                  ? <img src={lure.image_url} alt={lure.name} className="list-thumb" />
                  : <div className="list-thumb-placeholder">🎣</div>
                }
              </div>
              <div className="bl-lure bl-lure-name">{lure.name}</div>
              <div className="bl-brand">{lure.brands?.brand_name || '—'}</div>
              <div className="bl-category">{lure.lure_types?.type_name || '—'}</div>
              <div className="bl-attr">{lure.weight || '—'}</div>
              <div className="bl-attr">{lure.length || '—'}</div>
              <div className="bl-attr">{lure.depth_range || '—'}</div>
              <div className="bl-colors">
                {lure.variantCount > 0 ? lure.variantCount : '—'}
              </div>
              <div className={`bl-rating${lure.avgRating !== null ? ' rating-avg' : ' rating-empty'}`}>
                {lure.avgRating !== null ? formatRating(lure.avgRating) : '—'}
              </div>
            </Link>
          ))}
        </div>
      ) : (
        /* ─── GALLERY VIEW ─── */
        <>
          <div className="hitlist-sort-bar" style={{ marginBottom: '1rem' }}>
            <span className="sort-label">Sort:</span>
            {GALLERY_SORT_OPTIONS.map(({ key, label }) => (
              <button
                key={key}
                className={`sort-btn${sortColumn === key ? ' active' : ''}`}
                onClick={() => handleSort(key)}
              >
                {label}{sortColumn === key && <span> {sortDirection === 'asc' ? '▲' : '▼'}</span>}
              </button>
            ))}
            {sortColumn && (
              <button className="sort-btn" onClick={() => setSortColumn(null)}>✕ Clear</button>
            )}
          </div>

          <div className="collection-gallery">
            {filteredLures.map(lure => (
              <Link
                key={lure.base_lure_id}
                to={`/lure/${lure.base_lure_id}`}
                className="gallery-card browse-gallery-card"
              >
                <div className="gallery-image-wrap">
                  {lure.image_url ? (
                    <img src={lure.image_url} alt={lure.name} className="gallery-image" />
                  ) : (
                    <div className="gallery-placeholder">🎣</div>
                  )}
                  {lure.avgRating !== null && (
                    <span
                      className="gallery-status-badge gallery-rating-badge"
                      title={`${lure.reviewCount} ${lure.reviewCount === 1 ? 'review' : 'reviews'}`}
                    >
                      ★ {formatRating(lure.avgRating)}
                    </span>
                  )}
                </div>
                <div className="gallery-info">
                  <div className="gallery-lure-name">{lure.name}</div>
                  <div className="gallery-color">{lure.brands?.brand_name}</div>
                  <div className="gallery-meta">
                    {lure.lure_types?.type_name}
                    {lure.variantCount > 0 && ` · ${lure.variantCount} color${lure.variantCount !== 1 ? 's' : ''}`}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export default BrowseLures
